import * as JurorRepository from './repositories/JurorRepository';
import * as CaseRepository from './repositories/CaseRepository';
import SyncEngine, { schedulePendingQueueProcess } from './sync/SyncEngine';
import UndoManager from './undoManager';
import {
  isSeatFilledInJuryBox,
} from '../util/juror-helpers';
import {
  getSeatLayout,
  getSeatLayoutByNumber,
  getSeatNumbers,
  findFirstOpenSeat,
  assignJurorsToLayout,
} from '../util/seatLayout';

function getJurorId(juror) {
  return juror._id || juror.id;
}

function mergeDemographics(existingDemographics = [], demographics = []) {
  const updatedDemos = [...existingDemographics];

  for (const demo of demographics) {
    const idx = updatedDemos.findIndex(d => d.name === demo.name);
    if (idx >= 0) {
      updatedDemos[idx] = demo;
    } else {
      updatedDemos.push(demo);
    }
  }

  return updatedDemos;
}

function appendCommentUpdates(juror, commentText, commentsStyles = []) {
  let newComments = juror.comments || '';
  const offset = newComments.length;

  if (newComments.length > 0) {
    newComments += '\n' + commentText;
  } else {
    newComments = commentText;
  }

  const newStyles = [...(juror.commentsStyles || [])];
  for (const style of commentsStyles) {
    newStyles.push({
      ...style,
      offset: offset + style.offset + (juror.comments?.length ? 1 : 0),
    });
  }

  return {
    comments: newComments,
    commentsStyles: newStyles,
  };
}

/**
 * Apply multiple juror updates in one Dexie transaction and sync pass.
 * Prefer this over repeated updateJuror() calls whenever more than one
 * juror (or the same juror twice) needs to change.
 *
 * Recorded as one undo entry by default; pass { undoable: false } for
 * writes that should not enter the undo history.
 * @param {Array<{ jurorId: string, updates: Object }>} changes
 * @param {{ undoable?: boolean, undoLabel?: string }} [options]
 * @returns {Promise<Array<Object>>}
 */
export async function updateJurors(changes, options = {}) {
  if (!changes.length) return [];
  const { undoable = true, undoLabel } = options;

  let undoOps = null;
  if (undoable) {
    undoOps = [];
    for (const change of changes) {
      const op = await UndoManager.captureUpdateOp(change.jurorId, change.updates);
      if (op) undoOps.push(op);
    }
  }

  const updated = await JurorRepository.updateMany(changes);

  if (undoOps && undoOps.length > 0) {
    const label = undoLabel ||
      (undoOps.length === 1 ? 'Edit juror' : `Edit ${undoOps.length} jurors`);
    UndoManager.record(undoOps[0].caseId, label, undoOps);
  }

  schedulePendingQueueProcess();
  return updated;
}

/**
 * Database Action Helpers
 * 
 * These functions replace Redux async actions for data operations.
 * They write to Dexie, queue pending operations, and trigger sync.
 */

// ============ JUROR ACTIONS ============

/**
 * Create a new juror
 * @param {string} caseId - Case ID
 * @param {Object} jurorData - Juror data
 * @returns {Promise<Object>} - Created juror with local ID
 */
export async function createJuror(caseId, jurorData) {
  const juror = await JurorRepository.create(caseId, jurorData);
  schedulePendingQueueProcess();
  return juror;
}

/**
 * Create multiple jurors at once
 * @param {string} caseId - Case ID
 * @param {Array<Object>} jurorsData - Array of juror data objects
 * @returns {Promise<Array<Object>>} - Created jurors
 */
export async function createJurors(caseId, jurorsData) {
  const results = [];
  
  for (const jurorData of jurorsData) {
    const juror = await createJuror(caseId, jurorData);
    results.push(juror);
  }
  
  return results;
}

/**
 * Update a juror
 *
 * Recorded in undo history by default; pass { undoable: false } for
 * background writes (e.g. the juror dialog's auto-save, which has its own
 * in-dialog undo).
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {Object} updates - Fields to update
 * @param {{ undoable?: boolean, undoLabel?: string }} [options]
 * @returns {Promise<Object>} - Updated juror
 */
export async function updateJuror(caseId, jurorId, updates, options = {}) {
  const { undoable = true, undoLabel = 'Edit juror', unseatConflicts = true } = options;

  const undoOp = undoable
    ? await UndoManager.captureUpdateOp(jurorId, updates)
    : null;

  const updated = await JurorRepository.update(jurorId, updates, { unseatConflicts });

  if (undoOp && updated) {
    UndoManager.record(undoOp.caseId, undoLabel, [undoOp]);
  }

  schedulePendingQueueProcess();
  return updated;
}

export async function moveSeat(caseId, jurorId, targetSeatNumber, mode = 'jury-box') {
  const undoOps = await JurorRepository.moveSeat(caseId, jurorId, targetSeatNumber, mode);
  if (undoOps && undoOps.length > 0) {
    UndoManager.record(caseId, 'Move juror', undoOps);
  }
  schedulePendingQueueProcess();
}

/**
 * Delete a juror. Undo recreates it from a snapshot.
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {{ undoable?: boolean }} [options]
 */
export async function deleteJuror(caseId, jurorId, options = {}) {
  const { undoable = true } = options;
  const snapshot = undoable ? await JurorRepository.getById(jurorId) : null;

  await JurorRepository.remove(jurorId);

  if (snapshot) {
    UndoManager.record(caseId, 'Delete juror', [
      { type: 'delete', jurorId: snapshot._id, caseId, snapshot },
    ]);
  }
  schedulePendingQueueProcess();
}

/**
 * Dismiss a juror (mark as discarded)
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {Object} dismissInfo - { reason, discarder, mode }
 */
export async function dismissJuror(caseId, jurorId, dismissInfo) {
  const { reason, discarder, mode = 'jury-box' } = dismissInfo;
  
  const updates = mode === 'jury-box'
    ? { discarded: true, discardedReason: reason, discarder }
    : { alternateDiscarded: true, discardedReason: reason, discarder };

  return updateJuror(caseId, jurorId, updates, { undoLabel: 'Dismiss juror' });
}

/**
 * Restore a juror from dismissed state
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {string} mode - 'jury-box' or 'alternates'
 */
export async function restoreJuror(caseId, jurorId, mode = 'jury-box') {
  const updates = mode === 'jury-box'
    ? { discarded: false, discardedReason: null, discarder: null }
    : { alternateDiscarded: false, discardedReason: null, discarder: null };

  return updateJuror(caseId, jurorId, updates, { undoLabel: 'Restore juror' });
}

/**
 * Move juror to a seat in the jury box
 * @param {string} caseId - Case ID  
 * @param {string} jurorId - Juror ID
 * @param {number} seatNumber - Seat number
 * @param {string} mode - 'jury-box' or 'alternates'
 */
export async function seatJuror(caseId, jurorId, seatNumber, mode = 'jury-box') {
  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const changes = [];

  const currentlySeated = await JurorRepository.getJurorBySeat(caseId, seatNumber, mode);
  if (currentlySeated && getJurorId(currentlySeated) !== jurorId) {
    changes.push({
      jurorId: getJurorId(currentlySeated),
      updates: { [seatField]: null },
    });
  }

  changes.push({ jurorId, updates: { [seatField]: seatNumber } });

  const updated = await updateJurors(changes, { undoLabel: 'Seat juror' });
  return updated.find(j => getJurorId(j) === jurorId) || updated[updated.length - 1];
}

/**
 * Move juror back to pool (unseat)
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {string} mode - 'jury-box' or 'alternates'
 */
export async function unseatJuror(caseId, jurorId, mode = 'jury-box') {
  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  return updateJuror(caseId, jurorId, { [seatField]: null }, { undoLabel: 'Move juror to pool' });
}

/**
 * Build alert copy when a restored juror must go to the pool.
 * @param {'jury-box'|'alternates'} mode
 */
export function buildRestoreToPoolAlert(mode = 'jury-box') {
  const panel = mode === 'jury-box' ? 'jury box' : 'alternates panel';
  const title = mode === 'jury-box' ? 'Jury box full' : 'Alternates full';

  return {
    message: `The ${panel} is full. The juror has been restored to the jury pool instead.`,
    title,
    variant: 'info',
  };
}

/**
 * Restore juror to jury box (from pool or dismissed)
 * Reuses the juror's previous seat when available; otherwise uses the lowest
 * open seat, or the jury pool if the box is full.
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {string} mode - 'jury-box' or 'alternates'
 * @returns {Promise<{ updated: Object, placedInPool: boolean }>}
 */
export async function restoreJurorToBox(caseId, jurorId, mode = 'jury-box') {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) throw new Error('Case not found');

  const juror = await JurorRepository.getById(jurorId);
  if (!juror) throw new Error('Juror not found');

  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const discardField = mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';
  const jurors = await JurorRepository.getForCase(caseId);
  const layoutSeats = getSeatNumbers(caseData, mode);

  let seatNumber = juror[seatField];

  // The remembered seat must still exist in the layout (it may have been
  // renumbered while the juror was dismissed) and be unoccupied.
  if (seatNumber && layoutSeats.includes(seatNumber)) {
    if (isSeatFilledInJuryBox(jurors, seatNumber, mode)) {
      seatNumber = findFirstOpenSeat(caseData, jurors, mode);
    }
  } else {
    seatNumber = findFirstOpenSeat(caseData, jurors, mode);
  }

  const placedInPool = seatNumber == null;

  const updated = await updateJuror(caseId, jurorId, {
    [seatField]: seatNumber,
    [discardField]: false,
    discardedReason: null,
    discarder: null,
  }, { undoLabel: 'Restore juror' });

  if (placedInPool) {
    console.log(
      `Restored juror ${jurorId} to jury pool; ${mode} has no open seats`
    );
  }

  return { updated, placedInPool };
}

/**
 * Update multiple jurors' demographics
 * @param {string} caseId - Case ID
 * @param {Array<string>} jurorIds - Juror IDs
 * @param {Array<Object>} demographics - Demographics to set
 */
export async function updateJurorsDemographics(caseId, jurorIds, demographics) {
  const changes = [];

  for (const jurorId of jurorIds) {
    const juror = await JurorRepository.getById(jurorId);
    if (!juror) continue;

    changes.push({
      jurorId,
      updates: {
        demographics: mergeDemographics(juror.demographics, demographics),
      },
    });
  }

  return updateJurors(changes, { undoLabel: 'Multi-juror edit' });
}

/**
 * Append comment to multiple jurors
 * @param {string} caseId - Case ID
 * @param {Array<string>} jurorIds - Juror IDs
 * @param {string} commentText - Comment to append
 * @param {Array} commentsStyles - Styles for the comment
 */
export async function appendJurorsComment(
  caseId,
  jurorIds,
  commentText,
  commentsStyles = []
) {
  const changes = [];

  for (const jurorId of jurorIds) {
    const juror = await JurorRepository.getById(jurorId);
    if (!juror) continue;

    changes.push({
      jurorId,
      updates: appendCommentUpdates(juror, commentText, commentsStyles),
    });
  }

  return updateJurors(changes, { undoLabel: 'Multi-juror comment' });
}

/**
 * Apply demographics and/or comment updates to multiple jurors atomically.
 * @param {string} caseId - Case ID
 * @param {Array<string>} jurorIds - Juror IDs
 * @param {{ demographics?: Array<Object>, commentText?: string, commentsStyles?: Array }} edits
 */
export async function multiJurorEdit(caseId, jurorIds, edits = {}) {
  const { demographics = [], commentText = '', commentsStyles = [] } = edits;
  const hasDemographics = demographics.length > 0;
  const hasComment = Boolean(commentText?.length);

  if (!hasDemographics && !hasComment) return [];

  const changes = [];

  for (const jurorId of jurorIds) {
    const juror = await JurorRepository.getById(jurorId);
    if (!juror) continue;

    const updates = {};

    if (hasDemographics) {
      updates.demographics = mergeDemographics(juror.demographics, demographics);
    }

    if (hasComment) {
      Object.assign(
        updates,
        appendCommentUpdates(juror, commentText, commentsStyles)
      );
    }

    if (Object.keys(updates).length > 0) {
      changes.push({ jurorId, updates });
    }
  }

  return updateJurors(changes, { undoLabel: 'Multi-juror edit' });
}

// ============ JURY BOX ACTIONS ============

/**
 * Empty the jury box - move all jurors to pool
 * @param {string} caseId - Case ID
 */
export async function emptyJuryBox(caseId) {
  const jurors = await JurorRepository.getForCase(caseId);
  const changes = jurors
    .filter(j => j.seatNumber && !j.discarded)
    .map(j => ({ jurorId: getJurorId(j), updates: { seatNumber: null } }));

  await updateJurors(changes, { undoLabel: 'Empty jury box' });
}

/**
 * Empty alternates - move all to pool
 * @param {string} caseId - Case ID
 */
export async function emptyAlternates(caseId) {
  const jurors = await JurorRepository.getForCase(caseId);
  const changes = jurors
    .filter(j => j.alternateSeatNumber && !j.alternateDiscarded)
    .map(j => ({
      jurorId: getJurorId(j),
      updates: { alternateSeatNumber: null },
    }));

  await updateJurors(changes, { undoLabel: 'Empty alternates' });
}

/**
 * Fill empty seats by sliding jurors over
 * @param {string} caseId - Case ID
 * @param {string} mode - 'jury-box' or 'alternates'
 */
export async function fillEmptySpaces(caseId, mode = 'jury-box') {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) return;

  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';

  // Compact jurors toward the lowest seat NUMBERS: whoever is in seat 4
  // slides into seat 3 if it's open, regardless of where those seats sit
  // physically in the custom layout.
  const layout = getSeatLayoutByNumber(caseData, mode);
  const jurors = await JurorRepository.getForCase(caseId);
  const seatMap = {};
  for (const j of jurors) {
    const seat = j[seatField];
    if (seat && !j.discarded && !j.alternateDiscarded) {
      seatMap[seat] = j;
    }
  }

  // Occupants in seat-number order, reassigned to the lowest numbers.
  const seatedInOrder = layout
    .map(s => seatMap[s.seatNumber])
    .filter(Boolean);

  const changes = [];
  layout.forEach((seat, idx) => {
    const expected = seatedInOrder[idx] || null;
    const current = seatMap[seat.seatNumber] || null;
    if (expected && (!current || getJurorId(current) !== getJurorId(expected))) {
      changes.push({
        jurorId: getJurorId(expected),
        updates: { [seatField]: seat.seatNumber },
      });
    }
  });

  await updateJurors(changes, { undoLabel: 'Slide jurors over' });
}

/**
 * Fill jury box from pool
 * @param {string} caseId - Case ID
 * @param {string} mode - 'jury-box' or 'alternates'
 */
export async function fillFromPool(caseId, mode = 'jury-box') {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) return;

  const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';

  // Get pool jurors sorted by created date
  const jurors = await JurorRepository.getForCase(caseId);
  const poolJurors = jurors
    .filter(j => !j.seatNumber && !j.alternateSeatNumber && !j.discarded && !j.alternateDiscarded)
    .sort((a, b) => (a.created || '').localeCompare(b.created || ''));

  // Build current seat map
  const seatMap = {};
  for (const j of jurors) {
    const seat = j[seatField];
    if (seat && !j.discarded && !j.alternateDiscarded) {
      seatMap[seat] = j;
    }
  }

  const changes = [];
  let poolIdx = 0;

  // Fill empty seats from the lowest seat number up (honors custom numbering).
  for (const seat of getSeatLayoutByNumber(caseData, mode)) {
    if (poolIdx >= poolJurors.length) break;
    if (!seatMap[seat.seatNumber]) {
      changes.push({
        jurorId: getJurorId(poolJurors[poolIdx]),
        updates: { [seatField]: seat.seatNumber },
      });
      poolIdx++;
    }
  }

  await updateJurors(changes, { undoLabel: 'Fill from pool' });
}

// ============ CASE ACTIONS ============

/**
 * Create a new case
 * @param {Object} caseData - Case data
 * @param {string} [creatorEmail] - Creator's email (from session)
 * @returns {Promise<Object>} - Created case
 */
export async function createCase(caseData, creatorEmail) {
  const newCase = await CaseRepository.create(caseData, creatorEmail);
  schedulePendingQueueProcess();
  return newCase;
}

/**
 * Update a case
 * @param {string} caseId - Case ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - Updated case
 */
export async function updateCase(caseId, updates) {
  const updated = await CaseRepository.update(caseId, updates);
  schedulePendingQueueProcess();
  return updated;
}

/**
 * Archive a case. Goes through the normal update/diff sync path so it works
 * offline and increments the server-side revision like any other edit.
 * Archived cases stay in Dexie; the case list filters them out by default.
 * @param {string} caseId - Case ID
 * @param {string} userEmail - Email recorded as archivedBy
 * @returns {Promise<Object>} - Updated case
 */
export async function archiveCase(caseId, userEmail) {
  return updateCase(caseId, {
    archived: true,
    archivedAt: new Date().toISOString(),
    archivedBy: userEmail || null,
  });
}

/**
 * Restore an archived case back to the active list.
 * @param {string} caseId - Case ID
 * @returns {Promise<Object>} - Updated case
 */
export async function restoreCase(caseId) {
  return updateCase(caseId, {
    archived: false,
    archivedAt: null,
    archivedBy: null,
  });
}

/**
 * Delete a case
 * @param {string} caseId - Case ID
 */
export async function deleteCase(caseId) {
  // Drop the WS subscription first so reconnects don't try to re-join the
  // deleted case's room (the server logs those as unauthorized subscribes).
  SyncEngine.unsubscribeFromCase(caseId);
  await CaseRepository.remove(caseId);
  schedulePendingQueueProcess();
}

/**
 * Add a spacer to case
 * @param {string} caseId - Case ID
 * @param {number} spaceNumber - Space number for the spacer
 */
export async function addSpacer(caseId, spaceNumber) {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) return;
  
  const spacers = caseData.spacers || [];
  if (!spacers.includes(spaceNumber)) {
    await updateCase(caseId, { spacers: [...spacers, spaceNumber] });
  }
}

/**
 * Remove a spacer from case
 * @param {string} caseId - Case ID
 * @param {number} spaceNumber - Space number to remove
 */
export async function removeSpacer(caseId, spaceNumber) {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) return;
  
  const spacers = (caseData.spacers || []).filter(s => s !== spaceNumber);
  await updateCase(caseId, { spacers });
}

/**
 * Assign a custom seat number to a grid space (or clear it back to the
 * default with seatNumber = null). The occupying juror, if any, follows the
 * seat: their stored seat number is updated to the new effective number so
 * they stay in the same on-screen position.
 *
 * Duplicate numbers are allowed (so seats can be relabeled in any order,
 * e.g. 3 -> 5 while 5 still exists) — conflicts are reported back and
 * highlighted in the UI instead of blocking the save.
 *
 * @param {string} caseId
 * @param {'jury-box'|'alternates'} mode
 * @param {number} spaceNumber - Grid space being renumbered
 * @param {number|null} seatNumber - New custom number, or null to reset
 * @returns {Promise<{seatNumber: number, conflicts: number[]}>}
 */
export async function setCustomSeatNumber(caseId, mode, spaceNumber, seatNumber) {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) throw new Error('Case not found');

  const layout = getSeatLayout(caseData, mode);
  const space = layout.find(s => s.spaceNumber === spaceNumber);
  if (!space) throw new Error('Unknown seat space');

  const oldNumber = space.seatNumber;

  const nextCustom = { ...(caseData.customSeatNumbers || {}) };
  const modeMap = { ...(nextCustom[mode] || {}) };
  delete modeMap[spaceNumber];
  delete modeMap[String(spaceNumber)];
  if (seatNumber != null) {
    modeMap[String(spaceNumber)] = seatNumber;
  }
  nextCustom[mode] = modeMap;

  // Report (but allow) duplicate effective numbers in the resulting layout.
  const nextLayout = getSeatLayout({ ...caseData, customSeatNumbers: nextCustom }, mode);
  const counts = new Map();
  for (const s of nextLayout) {
    counts.set(s.seatNumber, (counts.get(s.seatNumber) || 0) + 1);
  }
  const conflicts = [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([num]) => num);

  const newNumber = nextLayout.find(s => s.spaceNumber === spaceNumber).seatNumber;

  await updateCase(caseId, { customSeatNumbers: nextCustom });

  // The juror in this seat keeps their position under the new number.
  if (oldNumber !== newNumber) {
    const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
    const jurors = await JurorRepository.getForCase(caseId);
    // Resolve the occupant with the same deterministic assignment the grid
    // renders with (pre-change layout). A bare stored-number match is
    // ambiguous while two spaces share a number mid-renumbering and could
    // move the juror the user sees in a DIFFERENT seat.
    const assignment = assignJurorsToLayout(layout, jurors, mode);
    const occupant = assignment.get(spaceNumber);
    if (occupant) {
      // Not undoable: the case-level renumbering isn't in the undo stack,
      // so reverting just the juror's seat would desync the two. No conflict
      // eviction: the occupant keeps their chair under a new label, so a
      // juror elsewhere holding the same number must not be unseated.
      await updateJuror(caseId, getJurorId(occupant), { [seatField]: newNumber }, { undoable: false, unseatConflicts: false });
    }
  }

  return { seatNumber: newNumber, conflicts };
}

/**
 * Reset all custom seat numbers for a mode back to the defaults.
 *
 * With `keepJurorsSeated` (default), every occupant keeps their chair:
 * jurors are resolved to spaces with the same deterministic assignment the
 * grid renders with, then renumbered to their space's default number in one
 * batch (no conflict eviction — defaults are unique by construction).
 *
 * With `keepJurorsSeated: false`, only the seat labels reset: jurors keep
 * their stored numbers and follow them to whichever space carries that
 * number under the default layout.
 *
 * @param {string} caseId
 * @param {'jury-box'|'alternates'} mode
 * @param {{ keepJurorsSeated?: boolean }} [options]
 */
export async function resetCustomSeatNumbers(caseId, mode, options = {}) {
  const { keepJurorsSeated = true } = options;
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) throw new Error('Case not found');

  const customMap = caseData.customSeatNumbers?.[mode] || {};
  if (Object.keys(customMap).length === 0) return;

  const changes = [];
  if (keepJurorsSeated) {
    // Resolve occupants against the pre-reset layout.
    const layout = getSeatLayout(caseData, mode);
    const jurors = await JurorRepository.getForCase(caseId);
    const assignment = assignJurorsToLayout(layout, jurors, mode);

    const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
    for (const space of layout) {
      const occupant = assignment.get(space.spaceNumber);
      if (occupant && occupant[seatField] !== space.defaultSeatNumber) {
        changes.push({
          jurorId: getJurorId(occupant),
          updates: { [seatField]: space.defaultSeatNumber },
        });
      }
    }
  }

  await updateCase(caseId, {
    customSeatNumbers: { ...(caseData.customSeatNumbers || {}), [mode]: {} },
  });

  if (changes.length > 0) {
    // Not undoable for the same reason as setCustomSeatNumber: the case-level
    // renumbering isn't in the undo stack.
    await updateJurors(changes, { undoable: false });
  }
}

/**
 * Swap a juror between jury box and alternates
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID
 * @param {string} mode - Current mode ('jury-box' or 'alternates')
 */
export async function swapJurorToOther(caseId, jurorId, mode) {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) throw new Error('Case not found');

  const juror = await JurorRepository.getById(jurorId);
  if (!juror) throw new Error('Juror not found');

  const jurors = await JurorRepository.getForCase(caseId);

  const destinationMode = mode === 'jury-box' ? 'alternates' : 'jury-box';
  const oldSeatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const newSeatField = destinationMode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
  const destinationSeats = getSeatNumbers(caseData, destinationMode);

  let seatNumber = juror[oldSeatField];

  if (seatNumber && destinationSeats.includes(seatNumber)) {
    if (isSeatFilledInJuryBox(jurors, seatNumber, destinationMode)) {
      seatNumber = findFirstOpenSeat(caseData, jurors, destinationMode);
    }
  } else {
    seatNumber = findFirstOpenSeat(caseData, jurors, destinationMode);
  }

  return updateJuror(caseId, jurorId, {
    [oldSeatField]: null,
    [newSeatField]: seatNumber,
  }, { undoLabel: destinationMode === 'alternates' ? 'Swap to alternates' : 'Swap to jury box' });
}

/**
 * Clone a case and all its jurors
 * @param {string} caseId - Case ID to clone
 * @returns {Promise<Object>} - The newly created case
 */
export async function cloneCase(caseId) {
  const caseData = await CaseRepository.getById(caseId);
  if (!caseData) throw new Error('Case not found');

  const jurors = await JurorRepository.getForCase(caseId);

  const newCaseData = JSON.parse(JSON.stringify(caseData));
  delete newCaseData._id;
  delete newCaseData.id;
  newCaseData.name = 'Copy of ' + newCaseData.name;
  newCaseData.createdAt = new Date().toISOString();

  const createdCase = await createCase(newCaseData);
  const newCaseId = createdCase._id || createdCase.id;

  for (const juror of jurors) {
    const newJuror = JSON.parse(JSON.stringify(juror));
    delete newJuror._id;
    delete newJuror.id;
    await createJuror(newCaseId, newJuror);
  }

  return createdCase;
}

// ============ SYNC ACTIONS ============

/**
 * Manually trigger sync of pending operations
 */
export function triggerSync() {
  return SyncEngine.processPendingQueue();
}

/**
 * Fetch all cases from the server and store in Dexie
 * Used to populate the case list on initial load / refresh
 */
export function fetchAllCases() {
  return SyncEngine.fetchAllCases();
}

/**
 * Force full sync for a case
 * @param {string} caseId - Case ID
 */
export function forceFullSync(caseId) {
  return SyncEngine.performFullSync(caseId);
}

/**
 * Apply a template's categories/scores/rules to a case (merge, not clobber).
 * Online-only — see db/sync/templateApi.js and SyncEngine.applyTemplateToCase.
 * @param {string} caseId - Case ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} - The updated case (as returned by the server)
 */
export function applyTemplateToCase(caseId, templateId) {
  return SyncEngine.applyTemplateToCase(caseId, templateId);
}

export default {
  // Juror actions
  createJuror,
  createJurors,
  updateJuror,
  updateJurors,
  deleteJuror,
  dismissJuror,
  restoreJuror,
  seatJuror,
  unseatJuror,
  restoreJurorToBox,
  buildRestoreToPoolAlert,
  updateJurorsDemographics,
  appendJurorsComment,
  multiJurorEdit,
  
  // Jury box actions
  emptyJuryBox,
  emptyAlternates,
  fillEmptySpaces,
  fillFromPool,
  
  // Case actions
  createCase,
  updateCase,
  archiveCase,
  restoreCase,
  deleteCase,
  addSpacer,
  removeSpacer,
  setCustomSeatNumber,
  resetCustomSeatNumbers,
  swapJurorToOther,
  cloneCase,
  
  // Sync actions
  triggerSync,
  forceFullSync,
  fetchAllCases,
  applyTemplateToCase
};
